<script lang="ts">
  import { navigate } from '../router.svelte';
  import { fmtPrice, memberStatusMeta, parseTs, planParts, timeAgo } from '../admin';
  import { calendarShort } from '../revenue';
  import { isPinned } from '../pipelines';
  import type { PipelineCard } from '@api';
  import ActivityStrip from '../charts/ActivityStrip.svelte';
  import RoleJourney from '@ui/RoleJourney.svelte';
  import Avatar from '@ui/Avatar.svelte';
  import MemberHover from '@ui/MemberHover.svelte';

  // One member's card on the pipeline board. Under the state model every member holds
  // one, so a column can be thousands deep — the card has to answer "who is this and
  // are they warming up or cooling down" in one glance, at four rows per screen.
  //
  // FOUR LINES, fixed:
  //   1  avatar · name · momentum dot · plan
  //   2  the role-journey bar + the weekly activity strip (both the same components
  //      the members table renders)
  //   3  joined/first-seen · last active
  //   4  only when there is something to say — pinned, departed
  //
  // What is deliberately NOT here: the "open" outcome footer, which said the same word on
  // every card in every working column and so carried no information at all. The
  // diagnosis-cause, primary-play and draft-ready chips were also excluded here before
  // the win-back plan generator that fed them was removed altogether.
  let {
    card,
    nowMs = Date.now(),
    onOpen,
    onDragStart,
    onDragEnd,
    onUnpin,
    onRemove,
  }: {
    card: PipelineCard;
    nowMs?: number;
    onOpen?: ((cardId: number) => void) | undefined;
    onDragStart?: ((cardId: number) => void) | undefined;
    onDragEnd?: (() => void) | undefined;
    /** Release the pin. Raises the intent only — the page prompts for a reason. */
    onUnpin?: ((cardId: number) => void) | undefined;
    /** MANUAL BOARDS ONLY: take this card off the board. Absent on a computed board,
     *  where a removal would be undone by the next tick and the control would lie. */
    onRemove?: ((cardId: number) => void) | undefined;
  } = $props();

  const m = $derived(card.member);
  const name = $derived(m?.name ?? 'Unknown member');
  const status = $derived(memberStatusMeta(m?.status));
  const departed = $derived(status?.departed ? status : null);
  const pinned = $derived(isPinned(card, nowMs));

  // MOMENTUM lives on the name line as a coloured dot, not a word-badge. It is one of
  // five values and the strip beside it already shows the shape of their activity, so a
  // dot plus a tooltip carries it without spending a line on a chip.
  const MOMENTUM: Record<string, { dot: string; label: string }> = {
    rising: { dot: 'bg-success', label: 'Rising' },
    steady: { dot: 'bg-primary', label: 'Steady' },
    at_risk: { dot: 'bg-warning', label: 'At risk' },
    silent: { dot: 'bg-base-content/25', label: 'Silent' },
    departed: { dot: 'bg-error', label: 'Departed' },
  };
  const momentum = $derived(m?.stage ? MOMENTUM[m.stage] : null);

  // PLAN, compact. Payers get the violet pill (`.pill-paying`) — money is the fact most
  // worth spotting in a scan of a hundred cards — while free/unknown stay quiet and mono
  // so they read as background. `planParts` is the same formatter the members table's
  // PlanCell uses, so an amount can't render two ways on two screens. It returns null at
  // or below zero cents, so the pill carries an AMOUNT in exactly the cases `paying` is
  // true — the tinted card and the priced pill cannot disagree about who pays.
  //
  // `paying` also tints the whole tile (`.tint-paying`). Payers are no longer floated to
  // the top of every column — the sort a reader picks is now the whole ordering — so the
  // tint is what keeps a payer findable in a column ranked by something else. Same
  // `plan_amount_cents > 0` test the `most_paying`/`least_paying` sorts order by; a NULL
  // amount (no roster access) is unknown, not free, and is neither tinted nor ranked.
  //
  // TINT PRECEDENCE, highest first: pinned, departed, paying. Pinned wins because it
  // marks the resolver being held off — a live override outranks a standing attribute.
  // Departed wins over paying because their plan is history: the grey says "gone", and a
  // churned payer tinted like a live one would misread on the Churn board's last column.
  const plan = $derived(planParts(m?.plan_amount_cents, m?.plan_currency, m?.plan_interval));
  const paying = $derived((m?.plan_amount_cents ?? 0) > 0);
  const planLabel = $derived(
    !m
      ? null
      : paying && plan?.amount
        ? plan.amount + (plan.period ?? '')
        : m.plan_tier
          ? m.plan_tier
          : m.status
            ? 'Free'
            : null,
  );

  // CANCELLING, AND WHEN. They hit cancel but are still a member — and still paying —
  // until `ends_at`, so the price stays on the pill and the cancellation is stated beside
  // it rather than replacing it. Both facts matter and they are different facts: "$97/mo"
  // is what stops, "cancelled 3d ago" is why it is going to.
  //
  // The pill turns RED. It is `pill-paying` violet otherwise, which on this board reads
  // as "good, they pay" — and a member on their way out wearing the same badge as a happy
  // one is the single most misleading cell we could draw on a Cancelled column.
  //
  // KEYED ON `status`, NOT on the timestamp. An earlier version keyed this on
  // `cancelled_at` so the pill could never turn red and then say nothing — which got it
  // exactly backwards: Skool lists a member on its cancelling tab without always giving
  // us `billingCanceled`, so a real cancellation with no date rendered as a happy violet
  // payer. On prod that is 1 of 15 (Lionel Cade on onecall), and it is worse than a
  // missing label: the board's `segment=cancelled` filter reads `status` too, so the
  // filter said "cancelled" while the card it returned said "paying".
  //
  // The STATUS is the fact; the date is decoration. A departed member is excluded —
  // their plan is history and the card is already grey for it (tint precedence above).
  const cancelling = $derived(!!m && !m.departed_at && m.status === 'cancelling');
  // DECLINED IS NOT CANCELLED. Skool files both on its `cancelling` tab and only
  // `billingCanceled` separates them, so this reads the absence of a cancellation date
  // rather than the presence of the tab. The words matter more than the colour here:
  // "cancelled" tells an owner to win them back, when the member never chose to leave
  // and the actual fix is "your card was declined, please update it".
  const declinedAt = $derived(
    cancelling && m?.declined_at && !m?.cancelled_at ? m.declined_at : null,
  );
  const cancelledAt = $derived(
    cancelling && !declinedAt ? (m?.cancelled_at ?? null) : null,
  );

  // WHEN THE MONEY STOPS, for the 1-in-15 with no cancellation date. `ends_at` is the
  // current period's end — the date the membership actually lapses — and every cancelling
  // member on prod has one. It is arguably the more useful of the two facts anyway: "they
  // cancelled 23 days ago" is history, "this ends on 9 Sep" is a deadline.
  const endsAt = $derived(
    cancelling && !cancelledAt && !declinedAt ? (m?.ends_at ?? null) : null,
  );

  /** "3h ago", "23d ago" — tight, because this sits beside the member's NAME and the
   *  name is the more important of the two. The long form ("cancelled 23 days ago")
   *  pushed the right-hand block wide enough to truncate "Dan Mirolli" to "Dan Mi…".
   *
   *  Hours are kept rather than collapsed to "today" the way `revenue.agoShort` does: a
   *  cancellation two hours old is a save you can still make today, and one from
   *  yesterday morning is not — that is the whole reason to print the age at all.
   *  Takes `nowMs` so it re-renders with the card's own 30s tick. */
  function agoTight(iso: string, now: number): string {
    const secs = Math.max(0, (now - parseTs(iso).getTime()) / 1000);
    if (secs < 3600) return `${Math.max(1, Math.floor(secs / 60))}m ago`;
    if (secs < 172800) return `${Math.floor(secs / 3600)}h ago`;
    if (secs < 2592000) return `${Math.floor(secs / 86400)}d ago`;
    return `${Math.floor(secs / 2592000)}mo ago`;
  }

  // WHAT THEY HAVE ACTUALLY PAID, under the plan pill. `plan_amount_cents` is the
  // recurring price and says nothing about how long they have been paying it; this is the
  // total, so a member on £49 for two years reads as worth more than one on £297 for a
  // month — which is the fact you want when the column is Churned.
  //
  // Shown from `> 0` only: null is "we cannot see the members page" and 0 is "signed up,
  // never billed", and neither is a payment worth printing a currency symbol for.
  const paidLabel = $derived(
    (m?.ltv_cents ?? 0) > 0 ? fmtPrice(m!.ltv_cents!, m?.plan_currency) : null,
  );

  // SINCE WHEN. The join date where a roster sweep gives us one; otherwise their first
  // contribution, labelled differently so the two are never confused — without staff
  // access we only discover a member BY seeing them post, so "joined" would be a guess.
  const since = $derived(m?.joined_at ?? m?.first_active ?? null);
  const sinceLabel = $derived(m?.joined_at ? 'joined' : 'first seen');

  function open(e: MouseEvent): void {
    if ((e.target as HTMLElement | null)?.closest('[data-noopen]')) return;
    onOpen?.(card.card_id);
  }
  function openKey(e: KeyboardEvent): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen?.(card.card_id);
    }
  }
  function toProfile(): void {
    if (m) navigate(`/admin/community/members/${card.member_id}`);
  }
  function dragStart(e: DragEvent): void {
    e.dataTransfer?.setData('text/plain', String(card.card_id));
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    onDragStart?.(card.card_id);
  }
</script>

<div
  role="button"
  tabindex="0"
  draggable="true"
  class="group cursor-pointer rounded-box border p-2 shadow-sm transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/40 {pinned
    ? 'border-warning/45 bg-warning/[0.13]'
    : departed
      ? 'hair bg-base-200/60'
      : paying
        ? 'tint-paying'
        : 'hair bg-base-100'}"
  onclick={open}
  onkeydown={openKey}
  ondragstart={dragStart}
  ondragend={() => onDragEnd?.()}
>
  <!-- 1 · identity + momentum + plan -->
  <div class="flex items-center gap-2">
    {#if onRemove}
      <!-- Hover-only, like the pin release: a delete sitting permanently on every card
           is a misclick waiting to happen on a board people drag things around. -->
      <button
        data-noopen
        class="order-last shrink-0 rounded-field px-1 text-base-content/35 opacity-0 transition-opacity hover:text-error group-hover:opacity-100 focus-visible:opacity-100"
        title="Remove from this board"
        aria-label="Remove from this board"
        onclick={() => onRemove(card.card_id)}
      >✕</button>
    {/if}
    {#if m}
      <div data-noopen class="shrink-0">
        <MemberHover memberId={card.member_id}>
          <Avatar src={m.picture_profile} {name} size={26} memberId={card.member_id} />
        </MemberHover>
      </div>
    {:else}
      <Avatar src={null} {name} size={26} />
    {/if}

    <div class="flex min-w-0 flex-1 items-center gap-1.5">
      {#if momentum}
        <span
          class="h-1.5 w-1.5 shrink-0 rounded-full {momentum.dot}"
          title="Momentum: {momentum.label}"
        ></span>
      {/if}
      <button
        data-noopen
        class="truncate text-left text-[0.8rem] font-medium leading-tight enabled:hover:underline disabled:cursor-default"
        onclick={toProfile}
        disabled={!m}
        title={m ? name + ' — open profile' : 'Member no longer in this community'}
      ><span class="redact">{name}</span></button>
    </div>

    {#if planLabel}
      <div class="flex shrink-0 flex-col items-end gap-0.5">
        <span
          class="rounded-field px-1.5 py-0.5 font-mono text-[0.6rem] tabular-nums {paying ? 'redact ' : ''}{declinedAt
            ? 'bg-warning/15 font-semibold text-warning'
            : cancelling
            ? 'bg-error/12 font-semibold text-error'
            : paying
              ? 'pill-paying font-semibold'
              : 'text-base-content/45'}"
          title={cancelling
            ? `Cancelled${cancelledAt ? ` ${parseTs(cancelledAt).toLocaleString()}` : ''}${m?.ends_at ? ` · paid up to ${parseTs(m.ends_at).toLocaleDateString()}` : ''}`
            : paying
              ? 'Paying member'
              : 'Not paying'}
        >{planLabel}</span>

        <!-- THE SECOND LINE IS ALWAYS RENDERED, blank when there is nothing to say.
             `paid $X` only exists for members who have actually paid, and letting it
             vanish made those cards 4px shorter than their neighbours — measured across a
             real board as 118 vs 122, and again 139 vs 144 among departed ones: the same
             gap twice. That is not information, it is a ragged column.
             Reserved by RENDERING the row rather than by a tuned `min-h`: the height then
             follows the type scale automatically, where a magic number silently goes 1px
             wrong the next time this text size changes (it already did, at 2.15rem). -->
        <span
          class="rounded-field px-1.5 py-px font-mono text-[0.55rem] tabular-nums
            {paidLabel ? 'redact pill-paid' : 'invisible'}"
          aria-hidden={paidLabel ? undefined : 'true'}
          title={paidLabel ? 'Paid to date, at whatever price they signed up on' : undefined}
        >{paidLabel ? `paid ${paidLabel}` : '\u00a0'}</span>
      </div>
    {/if}
  </div>

  <!-- 2 · the role arc, then twelve months of contribution -->
  {#if m?.journey.length}
    <!-- THE SAME role-journey bar the members table, the hovercard and the member page
         render, through the same component and the same `journey` block — one arc
         computed once, not a second visualisation of the same fact. `dense` is the
         variant built for narrow contexts, which is what a 17rem column is.
         Above the strip on purpose: the strip is twelve months of VOLUME, this is the
         standing that volume produced, and standing is what the columns are about. -->
    <div class="mt-1.5">
      <RoleJourney journey={m.journey} dense />
    </div>
  {/if}
  <div class="mt-1.5">
    <ActivityStrip
      weeks={m?.activity_weeks ?? []}
      max={m?.activity_max ?? 0}
      height="h-2.5"
    />
  </div>

  <!-- 3 · since when · cancelled · last active
       THE CANCELLATION SITS HERE, not under the price pill, and the reason is card
       height. This row exists on every card; the right-hand block does not, and giving it
       a third line made cancelled cards taller than the ones beside them — a column of
       ragged cards reads as a rendering fault long before anyone reads what the extra
       line says. Here it costs no height at all, and it is the right row anyway: this is
       already the card's list of time facts, so "cancelled 23d ago" is the same KIND of
       thing as "joined 1 month ago" and belongs beside it.
       The red price pill above carries the at-a-glance signal; this carries the when. -->
  <div class="mt-1.5 flex items-baseline justify-between gap-2 font-mono text-[0.58rem] text-base-content/50">
    <span class="flex min-w-0 items-baseline gap-1.5">
      {#if since}
        <span class="truncate" title="{sinceLabel} {timeAgo(since, nowMs)}">{sinceLabel} {timeAgo(since, nowMs)}</span>
      {:else}
        <span class="text-base-content/35">—</span>
      {/if}
      {#if declinedAt}
        <!-- Amber, not the cancellation red: this is the most RECOVERABLE money on the
             board — nobody decided to leave, a card stopped working. -->
        <span
          class="shrink-0 font-medium text-warning"
          title="Payment declined {parseTs(declinedAt).toLocaleString()} — not a cancellation"
        >· declined {agoTight(declinedAt, nowMs)}</span>
      {:else if cancelledAt}
        <span
          class="shrink-0 font-medium text-error/85"
          title="Cancelled {parseTs(cancelledAt).toLocaleString()}"
        >· cancelled {agoTight(cancelledAt, nowMs)}</span>
      {:else if endsAt}
        <!-- Cancelled, but Skool gave us no date for it. Say when it ENDS rather than
             saying nothing — the card must never look like a happy payer. -->
        <span
          class="shrink-0 font-medium text-error/85"
          title="Cancelled · paid up to {parseTs(endsAt).toLocaleDateString()}"
        >· ends {calendarShort(endsAt)}</span>
      {/if}
    </span>
    {#if m?.last_active}
      <span class="shrink-0" title="Last active">{timeAgo(m.last_active, nowMs)}</span>
    {/if}
  </div>

  <!-- 4 · only when there is something to say -->
  {#if pinned || departed}
    <div class="mt-1.5 flex items-center gap-1.5 text-[0.58rem] text-base-content/50">
      {#if pinned}
        <!-- A pinned card is a HUMAN OVERRIDE the resolver is being kept off, so it says
             so twice: the tile's warning tint, and this badge. On hover the badge becomes
             the release action, because "why is this card here" and "put it back" are the
             same thought and were previously two clicks apart via the drawer.
             `data-noopen` keeps that click off the card's own open handler. -->
        {#if onUnpin}
          <button
            data-noopen
            class="group/pin inline-flex items-center gap-1 rounded-field px-1 py-0.5 font-medium text-warning transition-colors hover:bg-warning/15"
            onclick={() => onUnpin(card.card_id)}
            title="Pinned by hand until {card.pinned_until ? new Date(card.pinned_until).toLocaleDateString() : 'the pin lapses'} — the resolver leaves it alone. Click to release it."
          >
            <svg class="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="17" x2="12" y2="22" /><path d="M5 17h14l-1.7-3.4a2 2 0 0 1-.3-1V5a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7.6a2 2 0 0 1-.3 1L5 17z" /></svg>
            <span class="group-hover/pin:hidden">pinned</span>
            <span class="hidden group-hover/pin:inline">unpin</span>
          </button>
        {:else}
          <span class="inline-flex items-center gap-1 px-1 py-0.5 font-medium text-warning" title="Pinned — moved by hand; the resolver leaves it for 7 days">
            <svg class="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="17" x2="12" y2="22" /><path d="M5 17h14l-1.7-3.4a2 2 0 0 1-.3-1V5a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7.6a2 2 0 0 1-.3 1L5 17z" /></svg>
            pinned
          </span>
        {/if}
      {/if}
      {#if departed}
        <span class="ml-auto truncate" title="{departed.label} — no longer a member">left {m?.departed_at ? timeAgo(m.departed_at, nowMs) : ''}</span>
      {/if}
    </div>
  {/if}

  {#if card.note}
    <!-- TWO LINES, CLAMPED. The note is on the board because it is what you scan for,
         but a card that grows with its note turns a column into a wall of prose — the
         drawer is one click away for the rest. -->
    <p class="redact mt-1.5 line-clamp-2 border-t hair pt-1.5 text-[0.68rem] leading-snug text-base-content/62"
       title="Open the card to read or edit this note">{card.note}</p>
  {/if}
</div>
